import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { motion } from 'framer-motion';
import { EnvelopeIcon, LockClosedIcon, EyeIcon, EyeSlashIcon, KeyIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { authAPI } from '../../services/api';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // 'login' | 'forgot' | 'reset'
  const [mode, setMode] = useState('login');
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const { login, user } = useAuth();
  const navigate = useNavigate();

  // If already signed in, don't show the login form — go straight to the app.
  useEffect(() => {
    if (user) navigate('/chat');
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(email, password);
    if (result.success) {
      navigate('/chat');
    }
    setLoading(false);
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const handleForgot = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    try {
      await authAPI.forgotPassword({ email: resetEmail });
      toast.success('If an account exists for that email, a reset code has been sent.');
      setMode('reset');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not send reset code.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    setResetLoading(true);
    try {
      const res = await authAPI.resetPassword({ email: resetEmail, otp: resetCode, newPassword });
      if (res.data?.success) {
        toast.success('Password updated. Please sign in with your new password.');
        setMode('login');
        setEmail(resetEmail);
        setPassword('');
        setResetCode('');
        setNewPassword('');
      } else {
        toast.error(res.data?.message || 'Could not reset password.');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not reset password.');
    } finally {
      setResetLoading(false);
    }
  };

  const headings = {
    login: 'Welcome Back',
    forgot: 'Reset your password',
    reset: 'Enter your reset code',
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full space-y-8 bg-white p-10 rounded-2xl shadow-xl"
      >
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-primary-600 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" /> Back to home
        </button>
        <div>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="mx-auto h-20 w-20 bg-primary-600 rounded-2xl flex items-center justify-center cursor-pointer"
            aria-label="AllocIQ home"
          >
            <span className="text-white text-2xl font-bold">AIQ</span>
          </button>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {headings[mode]}
          </h2>
          {mode === 'login' && (
            <p className="mt-2 text-center text-sm text-gray-600">
              Or{' '}
              <Link to="/register" className="font-medium text-primary-600 hover:text-primary-500">
                create a new account
              </Link>
            </p>
          )}
          {mode !== 'login' && (
            <p className="mt-2 text-center text-sm text-gray-600">
              {mode === 'forgot'
                ? "Enter your email and we'll send you a reset code."
                : 'Enter the code from your email and choose a new password.'}
            </p>
          )}
        </div>

        {/* ---------- LOGIN ---------- */}
        {mode === 'login' && (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <div className="relative">
                  <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input id="email" name="email" type="email" autoComplete="email" required
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="input-field pl-10" placeholder="you@example.com" />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <div className="relative">
                  <LockClosedIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input id="password" name="password" type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password" required
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    className="input-field pl-10 pr-10" placeholder="••••••••" />
                  <button type="button" onClick={togglePasswordVisibility} tabIndex="-1"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none">
                    {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input id="remember-me" name="remember-me" type="checkbox"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">Remember me</label>
              </div>
              <div className="text-sm">
                <button type="button"
                  onClick={() => { setResetEmail(email); setMode('forgot'); }}
                  className="font-medium text-primary-600 hover:text-primary-500">
                  Forgot your password?
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200">
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        )}

        {/* ---------- FORGOT (request code) ---------- */}
        {mode === 'forgot' && (
          <form className="mt-8 space-y-6" onSubmit={handleForgot}>
            <div>
              <label htmlFor="resetEmail" className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <div className="relative">
                <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input id="resetEmail" type="email" required value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="input-field pl-10" placeholder="you@example.com" />
              </div>
            </div>
            <button type="submit" disabled={resetLoading}
              className="w-full flex justify-center py-3 px-4 rounded-lg text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-sm font-medium transition-all">
              {resetLoading ? 'Sending...' : 'Send reset code'}
            </button>
            <button type="button" onClick={() => setMode('login')}
              className="w-full text-center text-sm text-gray-600 hover:text-primary-600">
              Back to sign in
            </button>
          </form>
        )}

        {/* ---------- RESET (enter code + new password) ---------- */}
        {mode === 'reset' && (
          <form className="mt-8 space-y-6" onSubmit={handleReset}>
            <div>
              <label htmlFor="resetCode" className="block text-sm font-medium text-gray-700 mb-1">Reset code</label>
              <div className="relative">
                <KeyIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input id="resetCode" type="text" inputMode="numeric" required value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  className="input-field pl-10 tracking-widest" placeholder="6-digit code" />
              </div>
            </div>
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">New password</label>
              <div className="relative">
                <LockClosedIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input id="newPassword" type={showPassword ? 'text' : 'password'} required value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input-field pl-10 pr-10" placeholder="At least 8 characters" />
                <button type="button" onClick={togglePasswordVisibility} tabIndex="-1"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none">
                  {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={resetLoading}
              className="w-full flex justify-center py-3 px-4 rounded-lg text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-sm font-medium transition-all">
              {resetLoading ? 'Updating...' : 'Reset password'}
            </button>
            <div className="flex items-center justify-between text-sm">
              <button type="button" onClick={() => setMode('forgot')} className="text-gray-600 hover:text-primary-600">
                Resend code
              </button>
              <button type="button" onClick={() => setMode('login')} className="text-gray-600 hover:text-primary-600">
                Back to sign in
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
};

export default Login;
